/**
 * Token Service
 * Microservice-compatible token management with JWT parsing, validation, and secure storage
 * 
 * SECURITY NOTES:
 * - Access tokens are stored in memory only (not persisted to storage)
 * - Refresh tokens are stored in encrypted storage for session persistence
 * - JWT parsing is INFORMATIONAL ONLY - never trust client-side parsed data
 * - All token validation must be performed server-side
 */

import { setSecureItem, getSecureItem, removeSecureItem } from '@hsus-dev/session-policy';
import { memoryTokenStorage } from './memoryTokenStorage.service';

export interface TokenPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  email?: string;
  name?: string;
  roles?: string[];
  [key: string]: unknown;
}

export interface TokenInfo {
  token: string;
  payload: TokenPayload;
  expiresAt: number | null;
  issuedAt: number | null;
  isExpired: boolean;
  isValid: boolean;
}

export interface TokenStorage {
  getAccessToken(): Promise<string | null>;
  getRefreshToken(): Promise<string | null>;
  setAccessToken(token: string): Promise<void>;
  setRefreshToken(token: string): Promise<void>;
  removeTokens(): Promise<void>;
}

/**
 * Hybrid token storage implementation
 * - Access tokens: Stored in memory only (not persisted)
 * - Refresh tokens: Stored in encrypted sessionStorage for persistence
 * 
 * Security Rationale:
 * - Access tokens are short-lived and should not survive page refreshes
 * - Memory storage reduces XSS attack surface
 * - Refresh tokens need persistence for session continuity
 */
class HybridTokenStorage implements TokenStorage {
  private readonly refreshTokenKey: string;

  constructor(refreshTokenKey: string = 'refresh_token') {
    this.refreshTokenKey = refreshTokenKey;
  }

  /**
   * Get access token from memory
   * Returns null if not in memory (e.g., after page refresh)
   */
  async getAccessToken(): Promise<string | null> {
    // Access tokens are in memory only - never persisted
    return memoryTokenStorage.getAccessToken();
  }

  /**
   * Get refresh token from encrypted storage
   */
  async getRefreshToken(): Promise<string | null> {
    try {
      // First check memory (may have been set during current session)
      const memoryToken = memoryTokenStorage.getRefreshToken();
      if (memoryToken) {
        return memoryToken;
      }
      
      // Fall back to encrypted storage for persistence
      return await getSecureItem(this.refreshTokenKey);
    } catch (error) {
      // Re-throw decryption errors so caller can handle corrupted data
      if (error instanceof Error && error.message === 'DECRYPTION_FAILED_CORRUPTED_DATA') {
        throw error;
      }
      return null;
    }
  }

  /**
   * Set access token in memory only (not persisted)
   */
  async setAccessToken(token: string): Promise<void> {
    // Store in memory only - will be lost on page refresh
    memoryTokenStorage.setAccessToken(token);
  }

  /**
   * Set refresh token in both memory and encrypted storage
   */
  async setRefreshToken(token: string): Promise<void> {
    try {
      // Store in memory for current session
      memoryTokenStorage.setRefreshToken(token);
      
      // Also store in encrypted storage for persistence across page refreshes
      await setSecureItem(this.refreshTokenKey, token);
    } catch (error) {
      console.error('Failed to store refresh token:', error);
      // Still keep in memory even if storage fails
    }
  }

  /**
   * Remove all tokens from both memory and storage
   */
  async removeTokens(): Promise<void> {
    try {
      // Clear memory
      memoryTokenStorage.clearAll();
      
      // Clear encrypted storage
      removeSecureItem(this.refreshTokenKey);
    } catch (error) {
      console.error('Failed to remove tokens:', error);
    }
  }
}

/**
 * Token Service for JWT parsing, validation, and storage
 */
export class TokenService {
  private storage: TokenStorage;
  private readonly expirationBufferMs: number;

  constructor(storage?: TokenStorage, expirationBufferMs: number = 60 * 1000) {
    // Default to hybrid storage (memory for access tokens, encrypted storage for refresh tokens)
    this.storage = storage ?? new HybridTokenStorage();
    this.expirationBufferMs = expirationBufferMs;
  }

  /**
   * Parse JWT token to extract payload
   * 
   * SECURITY WARNING: This is INFORMATIONAL ONLY
   * - Client-side JWT parsing is NOT a validation step
   * - Never trust data parsed from tokens on the client
   * - All token validation and authentication must be performed server-side
   * - This method is only for extracting metadata (expiration, user info) for UI purposes
   * - The server will validate token signature, expiration, and claims
   */
  parseToken(token: string): TokenPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const base64Url = parts[1];
      if (!base64Url) {
        return null;
      }

      // Decode base64url
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload) as TokenPayload;
    } catch (error) {
      console.error('Failed to parse JWT token:', error);
      return null;
    }
  }

  /**
   * Get token information including expiration and validity
   */
  getTokenInfo(token: string): TokenInfo {
    const payload = this.parseToken(token);
    const expiresAt = payload?.exp ? payload.exp * 1000 : null;
    const issuedAt = payload?.iat ? payload.iat * 1000 : null;
    const isExpired = this.isTokenExpired(token);
    const isValid = payload !== null && !isExpired;

    return {
      token,
      payload: payload ?? {},
      expiresAt,
      issuedAt,
      isExpired,
      isValid,
    };
  }

  /**
   * Check if token is expired (with buffer time)
   */
  isTokenExpired(token: string): boolean {
    const payload = this.parseToken(token);
    if (!payload || !payload.exp) {
      return true;
    }

    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const bufferTime = this.expirationBufferMs;

    return expirationTime - currentTime < bufferTime;
  }

  /**
   * Get token expiration time in milliseconds
   */
  getTokenExpirationTime(token: string): number | null {
    const payload = this.parseToken(token);
    if (!payload || !payload.exp) {
      return null;
    }
    return payload.exp * 1000;
  }

  /**
   * Get time until token expires in milliseconds
   */
  getTimeUntilExpiration(token: string): number | null {
    const expirationTime = this.getTokenExpirationTime(token);
    if (!expirationTime) {
      return null;
    }
    return Math.max(0, expirationTime - Date.now());
  }

  /**
   * Extract user information from token payload
   */
  extractUserInfo(token: string): {
    id: string;
    email?: string;
    name?: string;
    roles?: string[];
  } | null {
    const payload = this.parseToken(token);
    if (!payload) {
      return null;
    }

    return {
      id: payload.sub ?? payload.id as string ?? '',
      email: payload.email as string | undefined,
      name: payload.name as string | undefined,
      roles: payload.roles as string[] | undefined,
    };
  }

  /**
   * Storage methods (now async)
   */
  async getAccessToken(): Promise<string | null> {
    return await this.storage.getAccessToken();
  }

  async getRefreshToken(): Promise<string | null> {
    return await this.storage.getRefreshToken();
  }

  async setAccessToken(token: string): Promise<void> {
    await this.storage.setAccessToken(token);
  }

  async setRefreshToken(token: string): Promise<void> {
    await this.storage.setRefreshToken(token);
  }

  async removeTokens(): Promise<void> {
    await this.storage.removeTokens();
  }

  /**
   * Check if tokens exist and are valid
   */
  async hasValidToken(): Promise<boolean> {
    const token = await this.getAccessToken();
    if (!token) {
      return false;
    }
    return !this.isTokenExpired(token);
  }
}

/**
 * Token Service Factory
 * Manages singleton instance with reset capability for testing
 */
class TokenServiceFactory {
  private instance: TokenService | null = null;

  /**
   * Get or create the singleton TokenService instance
   */
  getInstance(storage?: TokenStorage, expirationBufferMs?: number): TokenService {
    if (!this.instance) {
      this.instance = new TokenService(storage, expirationBufferMs);
    }
    return this.instance;
  }

  /**
   * Reset the singleton instance (useful for testing)
   * Creates a new instance with optional custom storage and expiration buffer
   */
  reset(storage?: TokenStorage, expirationBufferMs?: number): TokenService {
    this.instance = new TokenService(storage, expirationBufferMs);
    return this.instance;
  }

  /**
   * Get the current instance without creating a new one
   */
  getCurrentInstance(): TokenService | null {
    return this.instance;
  }
}

// Create factory instance
const tokenServiceFactory = new TokenServiceFactory();

// Export singleton instance (backward compatibility)
export const tokenService = tokenServiceFactory.getInstance();

// Export factory for testing and advanced usage
export { tokenServiceFactory };




